import React from "react";
import moment from "moment";
import { Alert, Divider, Table, Tag } from "antd";
import * as XLSXStyle from "xlsx-js-style";
import { CheckCircleOutlined, CloseCircleOutlined, PauseCircleOutlined } from "@ant-design/icons";
import { ModalItem, ButtonItem } from "../../../../Components/Items";
import {
  celdaFecha,
  celdaMoneda,
  celdaNumero,
  celdaTexto,
  headerStyle,
} from "../../../../Components/ExcelEstilos";
import { construirPlantilla, filasPlantillaDePolizas } from "./plantillaImportarPolizas";

const ESTADOS = {
  creada: { etiqueta: "Creada", color: "green" },
  error: { etiqueta: "Con error", color: "red" },
  detenida: { etiqueta: "No se procesó (detenida)", color: "default" },
};

const etiquetaEstado = (estado) => ESTADOS[estado]?.etiqueta || estado || "";

const formatoMoneda = (valor) =>
  (Number(valor) || 0).toLocaleString("es-MX", { style: "currency", currency: "MXN" });

const COLUMNAS_EXCEL = [
  { titulo: "Póliza", ancho: 10, celda: (r) => celdaTexto(r.clave) },
  { titulo: "Filas del archivo", ancho: 16, celda: (r) => celdaTexto(r.filas) },
  { titulo: "Tipo de Póliza", ancho: 18, celda: (r) => celdaTexto(r.tipo) },
  { titulo: "Fecha", ancho: 12, celda: (r) => celdaFecha(r.fecha) },
  { titulo: "Concepto", ancho: 36, celda: (r) => celdaTexto(r.concepto) },
  { titulo: "Movimientos", ancho: 12, celda: (r) => celdaNumero(r.movimientos) },
  { titulo: "Total cargos", ancho: 16, celda: (r) => celdaMoneda(r.total) },
  { titulo: "Resultado", ancho: 22, celda: (r) => celdaTexto(etiquetaEstado(r.estado)) },
  { titulo: "Número", ancho: 10, celda: (r) => celdaNumero(r.numeroTipoPoliza ?? "") },
  { titulo: "Número por clase", ancho: 16, celda: (r) => celdaNumero(r.numeroTipoTipoPoliza ?? "") },
  { titulo: "Mensaje", ancho: 60, celda: (r) => celdaTexto(r.mensaje) },
];

const descargarResultados = (resultados) => {
  const hoja = XLSXStyle.utils.aoa_to_sheet([
    COLUMNAS_EXCEL.map((columna) => ({ v: columna.titulo, t: "s", s: headerStyle })),
    ...resultados.map((resultado) => COLUMNAS_EXCEL.map((columna) => columna.celda(resultado))),
  ]);
  hoja["!cols"] = COLUMNAS_EXCEL.map((columna) => ({ wch: columna.ancho }));
  hoja["!rows"] = [{ hpt: 26 }];

  const libro = XLSXStyle.utils.book_new();
  XLSXStyle.utils.book_append_sheet(libro, hoja, "Resultados");
  XLSXStyle.writeFile(libro, `resultadosImportarPolizas_${moment().format("YYYYMMDD_HHmm")}.xlsx`);
};

// La plantilla de importación con SOLO las pólizas que no se crearon, lista para corregir y
// volver a subir. Volver a subir el archivo original duplicaría las que sí entraron.
const descargarNoImportadas = (resultados, catalogos) => {
  const originales = resultados.filter((resultado) => resultado.original).map((resultado) => resultado.original);
  const libro = construirPlantilla(catalogos, filasPlantillaDePolizas(originales));
  XLSXStyle.writeFile(libro, `polizasNoImportadas_${moment().format("YYYYMMDD_HHmm")}.xlsx`);
};

const RESUMEN = [
  { key: "creada", label: "Pólizas creadas", icon: <CheckCircleOutlined style={{ color: "#52c41a" }} /> },
  { key: "error", label: "Con error (no se crearon)", icon: <CloseCircleOutlined style={{ color: "#ff4d4f" }} /> },
  {
    key: "detenida",
    label: "No se procesaron (se detuvo la importación)",
    icon: <PauseCircleOutlined style={{ color: "#8c8c8c" }} />,
  },
];

const ResultadosImportarPolizas = ({ visible, resultado, catalogos, cerrarModal }) => {
  const resultados = resultado?.resultados || [];
  const cuentaPor = (estado) => resultados.filter((r) => r.estado === estado).length;
  const noImportadas = resultados.filter((r) => r.original).length;
  // Una corrida que se cayó a media póliza no deja resultado de las que faltaban: no se puede
  // saber cuáles entraron sin revisar la lista de pólizas.
  const incompleta = resultado && (resultado.interrumpida || resultado.terminado === false);

  const columnas = [
    { title: "Póliza", dataIndex: "clave", key: "clave", width: 80 },
    { title: "Filas", dataIndex: "filas", key: "filas", width: 100 },
    { title: "Tipo", dataIndex: "tipo", key: "tipo", width: 120 },
    {
      title: "Fecha",
      dataIndex: "fecha",
      key: "fecha",
      width: 100,
      render: (valor) => (valor ? moment(valor, "YYYY-MM-DD").format("DD/MM/YYYY") : ""),
    },
    { title: "Concepto", dataIndex: "concepto", key: "concepto", width: 220 },
    {
      title: "Total",
      dataIndex: "total",
      key: "total",
      width: 120,
      align: "right",
      render: (valor) => formatoMoneda(valor),
    },
    {
      title: "Resultado",
      key: "estado",
      width: 170,
      render: (_, record) => <Tag color={ESTADOS[record.estado]?.color}>{etiquetaEstado(record.estado)}</Tag>,
    },
    { title: "Número", dataIndex: "numeroTipoPoliza", key: "numeroTipoPoliza", width: 80, align: "right" },
    { title: "Mensaje", dataIndex: "mensaje", key: "mensaje", width: 340 },
  ];

  return (
    <ModalItem title="Resultado de la importación" visible={visible} spinning={false} onCancel={cerrarModal} width={1200}>
      {resultado && (
        <>
          {resultado.detenida && (
            <Alert
              type="warning"
              showIcon
              style={{ marginBottom: 12 }}
              message="Se detuvo la importación"
              description="Las pólizas que no alcanzó a procesar salen como «No se procesó (detenida)». Descarga «No importadas» y sube sólo ese archivo para terminar."
            />
          )}

          {incompleta && (
            <Alert
              type="error"
              showIcon
              style={{ marginBottom: 12 }}
              message="La importación se interrumpió antes de terminar"
              description={`${resultado.error ? `${resultado.error}. ` : ""}Abajo está lo que sí se procesó (${resultados.length} de ${resultado.total || "?"} pólizas). Antes de volver a subir el archivo, revisa en la lista de Pólizas cuáles ya se crearon: volver a subirlas las duplica.`}
            />
          )}

          <div>
            {RESUMEN.filter((fila) => cuentaPor(fila.key) > 0).map((fila) => (
              <div key={fila.key} style={{ display: "flex", alignItems: "center", gap: 10, padding: "6px 0" }}>
                {fila.icon}
                <span style={{ flex: 1, color: "#595959" }}>{fila.label}</span>
                <span style={{ fontWeight: 500 }}>{cuentaPor(fila.key)}</span>
              </div>
            ))}
          </div>

          {noImportadas > 0 && !resultado.detenida && (
            <Alert
              type="warning"
              showIcon
              style={{ marginTop: 8 }}
              message={`${noImportadas} pólizas no se crearon`}
              description="Revisa el mensaje de cada una. Para reintentar, descarga «No importadas», corrígelas y sube sólo ese archivo: volver a subir el archivo completo duplicaría las que sí se crearon."
            />
          )}

          <Divider style={{ margin: "12px 0" }} />

          <Table
            size="small"
            rowKey={(record, index) => index}
            dataSource={resultados}
            columns={columnas}
            scroll={{ x: "max-content", y: 360 }}
            pagination={{ pageSize: 50, showSizeChanger: false }}
          />

          <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, marginTop: 16 }}>
            {noImportadas > 0 && (
              <ButtonItem onClick={() => descargarNoImportadas(resultados, catalogos)}>No importadas</ButtonItem>
            )}
            <ButtonItem onClick={() => descargarResultados(resultados)} disabled={resultados.length === 0}>
              Descargar Resultados
            </ButtonItem>
            <ButtonItem type="primary" onClick={cerrarModal}>
              Aceptar
            </ButtonItem>
          </div>
        </>
      )}
    </ModalItem>
  );
};

export default ResultadosImportarPolizas;
